import { Suspense } from 'react';
import { ErrorBoundary } from '@gonfalon/error-boundaries/ErrorBoundary';
import { useTreatmentImageUrl } from '@gonfalon/experiments/useTreatmentImageUrl';
import type { Variation } from '@gonfalon/flags/types';

import { ImageThumbnail } from '../ImageThumbnail';

type TreatmentImageProps = {
  /** Treatment image file id, as stored on the iteration treatment */
  imageFileId?: string;
  variation?: Variation;
  variationIndex?: number;
  size?: 'small' | 'medium' | 'large' | 'full';
  className?: string;
  alt?: string;
  allowPreviewClick?: boolean;
  enableTooltip?: boolean;
  onClick?: () => void;
};

const TreatmentImageContent = ({ imageFileId, ...thumbnailProps }: TreatmentImageProps & { imageFileId: string }) => {
  const src = useTreatmentImageUrl(imageFileId);
  return <ImageThumbnail src={src} {...thumbnailProps} />;
};

/**
 * Renders a treatment's image from its file id, resolving the presigned URL itself.
 *
 * Owns its Suspense and error boundaries so call sites only need the `imageFileId`
 * that already rides on the treatment. An unresolvable image degrades to the
 * placeholder rather than taking down the surrounding results or preview.
 */
export const TreatmentImage = ({ imageFileId, ...thumbnailProps }: TreatmentImageProps) => {
  if (!imageFileId) {
    return <ImageThumbnail {...thumbnailProps} />;
  }

  return (
    <ErrorBoundary
      severity="low"
      resetKeys={[imageFileId]}
      fallbackRender={() => <ImageThumbnail {...thumbnailProps} />}
    >
      <Suspense fallback={<ImageThumbnail isLoading {...thumbnailProps} />}>
        <TreatmentImageContent imageFileId={imageFileId} {...thumbnailProps} />
      </Suspense>
    </ErrorBoundary>
  );
};
